import { FC, useMemo } from 'react'
import { AISummaryWidget } from 'src/components/AISummaryWidget'
import { GenerateAISummaryPayload } from 'src/interfaces/aiSummary.interface'
import { buildCalendarContext } from 'src/utils/calendarContext'
import { useGenerateContentAnalysisSummaryMutation } from 'src/reactQuery/useGenerateContentAnalysisSummaryMutation'
import { useGetContentAnalysisSummaryQuery } from 'src/reactQuery/useGetContentAnalysisSummaryQuery'

export { ScopedSummarySection } from './ScopedSummarySection'

interface SummarySectionProps {
  analysisId: string
}

const CONTENT_ANALYSIS_COPY = {
  emptyText:
    'Generate your first summary to see AI-powered insights about your content performance.',
  footerText:
    'Summary generated by AI based on content performance data for the selected period.',
  generatingText:
    "This may take a few moments. We're scanning posts, and engagement data.",
}

// Content Analysis → Summary. The card itself is AISummaryWidget; like the
// Review Analysis and Social Listening widgets it is independent of the page
// date filter — the user picks the period in the widget's Time Filter.
export const SummarySection: FC<SummarySectionProps> = ({ analysisId }) => {
  const { data, isLoading, isError, refetch } =
    useGetContentAnalysisSummaryQuery({ id: analysisId })
  const generateMutation = useGenerateContentAnalysisSummaryMutation({
    id: analysisId,
  })

  // Same shared builder as the top-bar calendar on this page.
  const calendar = useMemo(
    () => buildCalendarContext({ analysisId, kind: 'content' }),
    [analysisId]
  )

  const handleGenerate = (payload: GenerateAISummaryPayload) =>
    generateMutation
      .mutateAsync(payload)
      .then((result) => result.data)
      .catch(() => undefined)

  return (
    <AISummaryWidget
      // Dots = days with posts in THIS analysis.
      calendar={calendar}
      copy={CONTENT_ANALYSIS_COPY}
      data={data?.data}
      isError={isError}
      isGenerating={generateMutation.isPending}
      isLoading={isLoading}
      onGenerate={handleGenerate}
      onRetry={() => refetch()}
    />
  )
}
